import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import {
  AlertTriangle, Calendar, ChevronLeft, ChevronRight, Clock, Edit3,
  IndianRupee, Plus, Search, Shield, Trash2, Users, X,
} from 'lucide-react';
import {
  fetchGovernanceOverview,
  fetchPenaltyKpis,
  fetchPenaltyRules,
  savePenaltyRule,
  deletePenaltyRule,
  fetchEmployeePenalties,
  fetchLeavePolicies,
  fetchEmployeeMasters,
  fetchShifts,
} from '../../services/governanceApi';
import IncomeTaxConfiguration from './IncomeTaxConfiguration';

const inputClass = 'w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500';
const PAGE_SIZE = 10;

const GOVERNANCE_SECTIONS = [
   { id: 'penalties', label: 'Penalty Rules', icon: AlertTriangle },
  // { id: 'leave-policies', label: 'Leave Policies', icon: Calendar },
  { id: 'tax-configuration', label: 'Tax Configuration', icon: IndianRupee },
  // { id: 'payroll-groups', label: 'Payroll Groups', icon: IndianRupee },
  // { id: 'attendance-policies', label: 'Attendance Policies', icon: Clock },
  // { id: 'shifts', label: 'Shift Governance', icon: Shield },
];

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(value || 0));

const emptyPenaltyRule = () => ({
  name: '',
  code: '',
  description: '',
  amount: 0,
  penaltyType: 'fixed_amount',
  employeeType: 'government',
  grade: 'A',
  role: 'All',
  payrollImpact: 'deduction',
  status: 'draft',
});

const normalizePenaltyRule = (rule) => ({
  id: rule.id,
  name: rule.name || '',
  code: rule.code || '',
  description: rule.description || '',
  amount: Number(rule.amount || 0),
  penaltyType: rule.penalty_type || rule.penaltyType || 'fixed_amount',
  employeeType: rule.employee_type || rule.employeeType || 'government',
  grade: rule.grade || 'A',
  role: rule.role || 'All',
  payrollImpact: rule.payroll_impact || rule.payrollImpact || 'deduction',
  status: rule.status || 'draft',
});

const serializePenaltyRule = (rule) => ({
  name: rule.name,
  code: rule.code,
  description: rule.description,
  amount: Number(rule.amount || 0),
  penalty_type: rule.penaltyType,
  employee_type: rule.employeeType,
  grade: rule.grade,
  role: rule.role,
  payroll_impact: rule.payrollImpact,
  status: rule.status,
});

const statusBadgeClass = (status) => {
  if (status === 'active') return 'bg-green-100 text-green-800';
  if (status === 'disabled') return 'bg-gray-200 text-gray-700';
  if (status === 'draft') return 'bg-amber-100 text-amber-800';
  return 'bg-gray-100 text-gray-700';
};

const Modal = ({ title, children, onClose, footer }) => (
  <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4">
    <div className="w-full max-w-3xl bg-white rounded-lg border border-gray-100 shadow-xl">
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700"><X className="w-5 h-5" /></button>
      </div>
      <div className="p-6 max-h-[70vh] overflow-auto">{children}</div>
      {footer && <div className="border-t border-gray-100 px-6 py-4 bg-gray-50">{footer}</div>}
    </div>
  </div>
);

const PenaltyForm = ({ initial, onCancel, onSave, saving }) => {
  const [form, setForm] = useState(normalizePenaltyRule(initial));
  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  return (
    <Modal
      title={initial?.id ? 'Edit Penalty Rule' : 'Add Penalty Rule'}
      onClose={onCancel}
      footer={
        <div className="flex justify-end gap-3">
          <button type="button" onClick={onCancel} className="px-4 py-2 rounded-lg border border-gray-200">Cancel</button>
          <button type="button" disabled={saving} onClick={() => onSave(form)} className="px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-50">Save</button>
        </div>
      }
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label><span className="text-sm font-semibold">Penalty Name</span><input className={`${inputClass} mt-1`} value={form.name} onChange={(e) => set('name', e.target.value)} /></label>
        <label><span className="text-sm font-semibold">Penalty Code</span><input className={`${inputClass} mt-1`} value={form.code} onChange={(e) => set('code', e.target.value.toUpperCase().replace(/\s+/g, '_'))} disabled={!!initial?.id} /></label>
        <label><span className="text-sm font-semibold">Description</span><input className={`${inputClass} mt-1`} value={form.description} onChange={(e) => set('description', e.target.value)} /></label>
        <label><span className="text-sm font-semibold">{form.penaltyType === 'percentage_based' ? 'Percentage' : 'Amount'}</span><input type="number" className={`${inputClass} mt-1`} value={form.amount} onChange={(e) => set('amount', Number(e.target.value || 0))} /></label>
        <label><span className="text-sm font-semibold">Penalty Type</span><select className={`${inputClass} mt-1`} value={form.penaltyType} onChange={(e) => set('penaltyType', e.target.value)}><option value="fixed_amount">Fixed</option><option value="percentage_based">Percentage</option></select></label>
        <label><span className="text-sm font-semibold">Employee Type</span><select className={`${inputClass} mt-1`} value={form.employeeType} onChange={(e) => set('employeeType', e.target.value)}><option value="government">Government</option><option value="hospital_staff">Hospital</option><option value="contractual">Contractual</option></select></label>
        <label><span className="text-sm font-semibold">Grade</span><select className={`${inputClass} mt-1`} value={form.grade} onChange={(e) => set('grade', e.target.value)}>{['A+', 'A', 'B', 'C', 'D'].map((g) => <option key={g} value={g}>{g}</option>)}</select></label>
        <label><span className="text-sm font-semibold">Applicable Roles</span><input className={`${inputClass} mt-1`} value={form.role} onChange={(e) => set('role', e.target.value)} /></label>
        <label><span className="text-sm font-semibold">Payroll Impact</span><select className={`${inputClass} mt-1`} value={form.payrollImpact} onChange={(e) => set('payrollImpact', e.target.value)}><option value="deduction">Deduction</option><option value="warning_only">Warning Only</option></select></label>
        <label><span className="text-sm font-semibold">Status</span><select className={`${inputClass} mt-1`} value={form.status} onChange={(e) => set('status', e.target.value)}><option value="active">Active</option><option value="draft">Draft</option><option value="disabled">Disabled</option></select></label>
      </div>
    </Modal>
  );
};

const PayrollGovernanceCenter = () => {
  const [activeSection, setActiveSection] = useState('penalties');
  const [loading, setLoading] = useState(true);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [overview, setOverview] = useState(null);
  const [kpis, setKpis] = useState({ active_penalties: 0, monthly_deductions: 0, employees_penalized: 0 });
  const [penalties, setPenalties] = useState([]);
  const [penaltyPage, setPenaltyPage] = useState(1);
  const [penaltyTotalPages, setPenaltyTotalPages] = useState(1);
  const [penaltySummaryPage, setPenaltySummaryPage] = useState(1);
  const [penaltySummaryTotalPages, setPenaltySummaryTotalPages] = useState(1);
  const [employeeAssignments, setEmployeeAssignments] = useState([]);

  const [leavePolicies, setLeavePolicies] = useState([]);
  const [masters, setMasters] = useState(null);
  const [shifts, setShifts] = useState([]);

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [impactFilter, setImpactFilter] = useState('all');
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState('all');
  const [activeModal, setActiveModal] = useState(null);

  const abortRef = useRef(null);
  const loadedSectionsRef = useRef(new Set());
  const skipFilterFetchRef = useRef(true);

  const showMessage = (msg, isError = false) => {
    if (isError) {
      setError(msg);
      setSuccess('');
    } else {
      setSuccess(msg);
      setError('');
    }
    setTimeout(() => { setError(''); setSuccess(''); }, 5000);
  };

  const loadOverviewAndKpis = useCallback(async (signal) => {
    const [overviewData, kpiData] = await Promise.all([
      fetchGovernanceOverview(signal),
      fetchPenaltyKpis(signal),
    ]);
    setOverview(overviewData);
    setKpis(kpiData);
  }, []);

  const loadPenalties = useCallback(async (page, signal) => {
    const params = { page: String(page), page_size: String(PAGE_SIZE) };
    if (search.trim()) params.search = search.trim();
    if (statusFilter !== 'all') params.status = statusFilter;
    if (impactFilter !== 'all') params.payroll_impact = impactFilter;
    if (employeeTypeFilter !== 'all') params.employee_type = employeeTypeFilter;
    const data = await fetchPenaltyRules(params, signal);
    setPenalties((data.items || []).map(normalizePenaltyRule));
    setPenaltyPage(data.page || 1);
    setPenaltyTotalPages(data.total_pages || 1);
  }, [employeeTypeFilter, impactFilter, search, statusFilter]);

  const loadEmployeeSummary = useCallback(async (page, signal) => {
    const data = await fetchEmployeePenalties(page, signal);
    setEmployeeAssignments(data.items || []);
    setPenaltySummaryPage(data.page || 1);
    setPenaltySummaryTotalPages(data.total_pages || 1);
  }, []);

  const loadLeavePolicies = useCallback(async (signal) => {
    const data = await fetchLeavePolicies({ page: '1', status: 'active' }, signal);
    setLeavePolicies(data.items || []);
  }, []);

  const loadMasters = useCallback(async (signal) => {
    const data = await fetchEmployeeMasters(signal);
    setMasters(data);
  }, []);

  const loadShifts = useCallback(async (signal) => {
    const data = await fetchShifts(true, signal);
    setShifts(data.shifts || []);
  }, []);

  const loadSection = useCallback(async (section, page = 1) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSectionLoading(true);
    try {
      if (section === 'penalties') {
        await Promise.all([
          loadPenalties(page, controller.signal),
          loadEmployeeSummary(1, controller.signal),
        ]);
      } else if (section === 'leave-policies') {
        await loadLeavePolicies(controller.signal);
      } else if (section === 'payroll-groups' || section === 'attendance-policies') {
        await loadMasters(controller.signal);
      } else if (section === 'shifts') {
        await loadShifts(controller.signal);
      } else if (section === 'tax-configuration') {
        // Handled by child component
      }
      loadedSectionsRef.current.add(section);
    } catch (err) {
      if (err.name !== 'AbortError') showMessage(err.message || 'Failed to load section', true);
    } finally {
      if (!controller.signal.aborted) setSectionLoading(false);
    }
  }, [loadEmployeeSummary, loadLeavePolicies, loadMasters, loadPenalties, loadShifts]);

  useEffect(() => {
    const controller = new AbortController();
    (async () => {
      setLoading(true);
      try {
        const results = await Promise.allSettled([
          loadOverviewAndKpis(controller.signal),
          loadPenalties(1, controller.signal),
          loadEmployeeSummary(1, controller.signal),
        ]);
        const failures = results.filter((r) => r.status === 'rejected' && r.reason?.name !== 'AbortError');
        if (failures.length === results.length) {
          showMessage(failures[0].reason?.message || 'Failed to load payroll governance', true);
        } else if (failures.length > 0) {
          showMessage(failures[0].reason?.message || 'Some governance data failed to load', true);
        }
        loadedSectionsRef.current.add('penalties');
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [loadOverviewAndKpis, loadEmployeeSummary, loadPenalties]);

  useEffect(() => {
    if (loading) return;
    const timer = setTimeout(() => {
      if (skipFilterFetchRef.current) {
        skipFilterFetchRef.current = false;
        return;
      }
      loadPenalties(1).catch((err) => {
        if (err.name !== 'AbortError') showMessage(err.message, true);
      });
    }, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [search, statusFilter, impactFilter, employeeTypeFilter, loading, loadPenalties]);

  useEffect(() => {
    if (loading || activeSection === 'penalties') return;
    if (!loadedSectionsRef.current.has(activeSection)) {
      loadSection(activeSection);
    }
  }, [activeSection, loadSection, loading]);

  const groupedEmployeeSummary = useMemo(() => {
    const grouped = {};
    employeeAssignments.forEach((row) => {
      const key = row.employee_id;
      if (!grouped[key]) {
        grouped[key] = { employeeName: row.employee_name || `Employee ${row.employee_id}`, penalties: [], total: 0 };
      }
      grouped[key].penalties.push(row.rule?.name || row.penalty_rule_id);
      grouped[key].total += Number(row.penalty_amount || 0);
    });
    return Object.entries(grouped).map(([employeeId, value]) => ({ employeeId, ...value }));
  }, [employeeAssignments]);

  const refreshAfterMutation = async () => {
    const controller = new AbortController();
    await Promise.all([
      loadOverviewAndKpis(controller.signal),
      loadPenalties(penaltyPage, controller.signal),
    ]);
  };

  const savePenalty = async (rule) => {
    try {
      setSaving(true);
      await savePenaltyRule({ id: rule.id, ...serializePenaltyRule(rule) });
      setActiveModal(null);
      await refreshAfterMutation();
      showMessage(rule.id ? 'Penalty rule updated' : 'Penalty rule created');
    } catch (err) {
      showMessage(err.message || 'Failed to save penalty rule', true);
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePenalty = async (ruleId) => {
    if (!window.confirm('Delete this penalty rule? Rules with historical assignments will be disabled instead.')) return;
    try {
      setSaving(true);
      const result = await deletePenaltyRule(ruleId);
      await refreshAfterMutation();
      if (result.action === 'disabled') {
        showMessage('Rule disabled because historical assignments exist');
      } else {
        showMessage('Penalty rule permanently deleted');
      }
    } catch (err) {
      showMessage(err.message || 'Failed to delete penalty rule', true);
    } finally {
      setSaving(false);
    }
  };

  const togglePenaltyStatus = async (rule) => {
    const nextStatus = rule.status === 'active' ? 'disabled' : 'active';
    await savePenalty({ ...rule, status: nextStatus });
  };

  const overviewCards = overview ? [
    { title: 'Active Penalty Rules', value: overview.active_penalty_rules, subtitle: `${overview.disabled_penalty_rules} disabled`, tone: 'bg-red-600', Icon: AlertTriangle },
    { title: 'Leave Policies', value: overview.active_leave_policies, subtitle: `${overview.active_leave_policy_masters} policy masters`, tone: 'bg-blue-600', Icon: Calendar },
    { title: 'Payroll Groups', value: overview.active_payroll_groups, subtitle: 'Active payroll categories', tone: 'bg-indigo-600', Icon: IndianRupee },
    { title: 'Attendance Policies', value: overview.active_attendance_policies, subtitle: `${overview.active_shifts} active shifts`, tone: 'bg-emerald-600', Icon: Clock },
  ] : [];

  const payrollGroups = masters?.payroll_groups || [];
  const attendancePolicies = masters?.attendance_policies || [];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Payroll Governance</h1>
        <p className="text-gray-600 text-sm mt-1">Backend-driven configuration for penalties, leave, payroll, attendance, and shifts</p>
      </div>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-4 py-3 text-sm">{error}</div>}
      {success && <div className="rounded-lg border border-green-200 bg-green-50 text-green-700 px-4 py-3 text-sm">{success}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {overviewCards.map((item) => (
          <motion.div key={item.title} whileHover={{ y: -2 }} className="bg-white rounded-lg p-4 shadow border border-gray-100">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">{item.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{item.value}</p>
                <p className="text-xs text-gray-500 mt-1">{item.subtitle}</p>
              </div>
              <div className={`p-2 rounded-lg ${item.tone}`}><item.Icon className="w-5 h-5 text-white" /></div>
            </div>
          </motion.div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 border-b border-gray-200 pb-1">
        {GOVERNANCE_SECTIONS.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setActiveSection(id)}
            className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors ${
              activeSection === id ? 'border-blue-600 text-blue-700 bg-blue-50' : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {activeSection === 'penalties' && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 border border-gray-100">
              <p className="text-sm text-gray-500">Active Penalties</p>
              <p className="text-2xl font-bold mt-1">{kpis.active_penalties}</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-100">
              <p className="text-sm text-gray-500">Monthly Deductions</p>
              <p className="text-2xl font-bold mt-1">{formatCurrency(kpis.monthly_deductions)}</p>
            </div>
            <div className="bg-white rounded-lg p-4 border border-gray-100">
              <p className="text-sm text-gray-500">Employees Penalized</p>
              <p className="text-2xl font-bold mt-1">{kpis.employees_penalized}</p>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow border border-gray-100 p-6">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900">Penalty Rules</h2>
              <button type="button" onClick={() => setActiveModal(emptyPenaltyRule())} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700">
                <Plus className="w-4 h-4" /> Add Penalty
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
              <div className="relative">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-3" />
                <input className={`${inputClass} pl-9`} placeholder="Search penalties" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <select className={inputClass} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="active">Active</option>
                <option value="draft">Draft</option>
                <option value="disabled">Disabled</option>
                <option value="all">All Status</option>
              </select>
              <select className={inputClass} value={impactFilter} onChange={(e) => setImpactFilter(e.target.value)}>
                <option value="all">All Impact</option>
                <option value="deduction">Deduction</option>
                <option value="warning_only">Warning Only</option>
              </select>
              <select className={inputClass} value={employeeTypeFilter} onChange={(e) => setEmployeeTypeFilter(e.target.value)}>
                <option value="all">All Employee Types</option>
                <option value="government">Government</option>
                <option value="hospital_staff">Hospital</option>
                <option value="contractual">Contractual</option>
              </select>
            </div>
            {sectionLoading ? (
              <div className="py-12 text-center text-gray-500">Loading...</div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-100">
                    <thead className="bg-gray-50">
                      <tr>{['Name', 'Code', 'Type', 'Amount/%', 'Employee Type', 'Grade', 'Impact', 'Status', 'Actions'].map((h) => (
                        <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">{h}</th>
                      ))}</tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {penalties.map((row) => (
                        <tr key={row.id} className="hover:bg-gray-50">
                          <td className="px-4 py-4"><p className="font-semibold text-gray-900">{row.name}</p><p className="text-xs text-gray-500">{row.description}</p></td>
                          <td className="px-4 py-4 text-sm">{row.code}</td>
                          <td className="px-4 py-4 text-sm">{row.penaltyType === 'percentage_based' ? 'Percentage' : 'Fixed'}</td>
                          <td className="px-4 py-4 text-sm font-semibold text-red-700">{row.penaltyType === 'percentage_based' ? `${row.amount}%` : formatCurrency(row.amount)}</td>
                          <td className="px-4 py-4 text-sm capitalize">{row.employeeType.replace('_', ' ')}</td>
                          <td className="px-4 py-4 text-sm">{row.grade}</td>
                          <td className="px-4 py-4 text-sm capitalize">{row.payrollImpact.replace('_', ' ')}</td>
                          <td className="px-4 py-4">
                            <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase ${statusBadgeClass(row.status)}`}>{row.status}</span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-2">
                              {row.status !== 'disabled' && (
                                <button type="button" disabled={saving} onClick={() => togglePenaltyStatus(row)} className="px-3 py-2 text-xs rounded-lg border border-blue-200 text-blue-700">
                                  {row.status === 'active' ? 'Deactivate' : 'Activate'}
                                </button>
                              )}
                              <button type="button" disabled={saving} onClick={() => setActiveModal(row)} className="px-3 py-2 text-xs rounded-lg border border-gray-200"><Edit3 className="w-3 h-3" /></button>
                              <button type="button" disabled={saving} onClick={() => handleDeletePenalty(row.id)} className="px-3 py-2 text-xs rounded-lg border border-red-200 text-red-700"><Trash2 className="w-3 h-3" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {penalties.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">No penalty rules match the current filters.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div className="flex justify-end items-center gap-2 mt-4">
                  <button type="button" disabled={penaltyPage <= 1} onClick={() => loadPenalties(penaltyPage - 1)} className="px-3 py-2 border border-gray-200 rounded-lg disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></button>
                  <span className="text-sm text-gray-600">Page {penaltyPage} of {penaltyTotalPages}</span>
                  <button type="button" disabled={penaltyPage >= penaltyTotalPages} onClick={() => loadPenalties(penaltyPage + 1)} className="px-3 py-2 border border-gray-200 rounded-lg disabled:opacity-50"><ChevronRight className="w-4 h-4" /></button>
                </div>
              </>
            )}
          </div>

          <div className="bg-white rounded-lg shadow border border-gray-100 p-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Employee Penalty Summary</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50"><tr>{['Employee', 'Applied Penalties', 'Total Deduction'].map((h) => <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">{h}</th>)}</tr></thead>
                <tbody className="divide-y divide-gray-100">
                  {groupedEmployeeSummary.map((row) => (
                    <tr key={row.employeeId} className="hover:bg-gray-50">
                      <td className="px-4 py-4 font-medium text-gray-900">{row.employeeName}</td>
                      <td className="px-4 py-4 text-sm text-gray-700">{row.penalties.join(', ')}</td>
                      <td className="px-4 py-4 font-bold text-red-700">{formatCurrency(row.total)}</td>
                    </tr>
                  ))}
                  {groupedEmployeeSummary.length === 0 && <tr><td colSpan={3} className="px-4 py-8 text-center text-gray-500">No employee penalties assigned.</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end items-center gap-2 mt-4">
              <button type="button" disabled={penaltySummaryPage <= 1} onClick={() => loadEmployeeSummary(penaltySummaryPage - 1)} className="px-3 py-2 border border-gray-200 rounded-lg disabled:opacity-50"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm text-gray-600">Page {penaltySummaryPage} of {penaltySummaryTotalPages}</span>
              <button type="button" disabled={penaltySummaryPage >= penaltySummaryTotalPages} onClick={() => loadEmployeeSummary(penaltySummaryPage + 1)} className="px-3 py-2 border border-gray-200 rounded-lg disabled:opacity-50"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </>
      )}

      {activeSection === 'leave-policies' && (
        <div className="bg-white rounded-lg shadow border border-gray-100 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Leave Policies</h2>
          <p className="text-sm text-gray-500 mb-4">Active leave governance rules from Leave Management APIs. Manage full CRUD in Leave Management.</p>
          {sectionLoading ? <div className="py-12 text-center text-gray-500">Loading...</div> : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>{['Code', 'Name', 'Leave Type', 'Employee Type', 'Grade', 'Days/Year', 'Carry Forward', 'Encashment', 'Status'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {leavePolicies.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{p.code}</td>
                      <td className="px-4 py-3 text-sm font-medium">{p.name}</td>
                      <td className="px-4 py-3 text-sm">{p.leave_type_name || '-'}</td>
                      <td className="px-4 py-3 text-sm capitalize">{p.employee_type}</td>
                      <td className="px-4 py-3 text-sm">{p.grade}</td>
                      <td className="px-4 py-3 text-sm">{p.days_allowed_per_year}</td>
                      <td className="px-4 py-3 text-sm">{p.carry_forward_allowed ? `Yes (${p.carry_forward_limit})` : 'No'}</td>
                      <td className="px-4 py-3 text-sm">{p.encashment_allowed ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold uppercase ${statusBadgeClass(p.status)}`}>{p.status}</span></td>
                    </tr>
                  ))}
                  {leavePolicies.length === 0 && <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-500">No active leave policies found.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeSection === 'tax-configuration' && (
        <IncomeTaxConfiguration />
      )}

      {activeSection === 'payroll-groups' && (
        <div className="bg-white rounded-lg shadow border border-gray-100 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Payroll Groups</h2>
          <p className="text-sm text-gray-500 mb-4">Payroll category groupings from Employee Master configuration.</p>
          {sectionLoading ? <div className="py-12 text-center text-gray-500">Loading...</div> : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50"><tr>{['Code', 'Name', 'Category', 'Status'].map((h) => <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">{h}</th>)}</tr></thead>
                <tbody>
                  {payrollGroups.map((g) => (
                    <tr key={g.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{g.code}</td>
                      <td className="px-4 py-3 text-sm font-medium">{g.name}</td>
                      <td className="px-4 py-3 text-sm">{g.payroll_category || '-'}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold uppercase ${statusBadgeClass(g.status)}`}>{g.status}</span></td>
                    </tr>
                  ))}
                  {payrollGroups.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">No payroll groups found.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeSection === 'attendance-policies' && (
        <div className="bg-white rounded-lg shadow border border-gray-100 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Attendance Policies</h2>
          <p className="text-sm text-gray-500 mb-4">Attendance governance bundles from Employee Master. Shift-level grace and hours are under Shift Governance.</p>
          {sectionLoading ? <div className="py-12 text-center text-gray-500">Loading...</div> : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50"><tr>{['Code', 'Name', 'Description', 'Status'].map((h) => <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">{h}</th>)}</tr></thead>
                <tbody>
                  {attendancePolicies.map((p) => (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{p.code}</td>
                      <td className="px-4 py-3 text-sm font-medium">{p.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-600">{p.description || (p.rules ? JSON.stringify(p.rules).slice(0, 80) : '-')}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold uppercase ${statusBadgeClass(p.status)}`}>{p.status}</span></td>
                    </tr>
                  ))}
                  {attendancePolicies.length === 0 && <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-500">No attendance policies found.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeSection === 'shifts' && (
        <div className="bg-white rounded-lg shadow border border-gray-100 p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Shift Governance</h2>
          <p className="text-sm text-gray-500 mb-4">Active shifts with timing and attendance rules. Full CRUD in Shift & Roster Management.</p>
          {sectionLoading ? <div className="py-12 text-center text-gray-500">Loading...</div> : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-100">
                <thead className="bg-gray-50">
                  <tr>{['Code', 'Name', 'Start', 'End', 'Hours', 'Late After (min)', 'Night Shift', 'Status'].map((h) => (
                    <th key={h} className="px-4 py-3 text-left text-xs font-semibold uppercase text-gray-500">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {shifts.map((s) => (
                    <tr key={s.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{s.code}</td>
                      <td className="px-4 py-3 text-sm font-medium">{s.name}</td>
                      <td className="px-4 py-3 text-sm">{s.start_time}</td>
                      <td className="px-4 py-3 text-sm">{s.end_time}</td>
                      <td className="px-4 py-3 text-sm">{s.total_hours}</td>
                      <td className="px-4 py-3 text-sm">{s.late_mark_after}</td>
                      <td className="px-4 py-3 text-sm">{s.is_night_shift ? 'Yes' : 'No'}</td>
                      <td className="px-4 py-3"><span className={`px-2 py-1 rounded-full text-xs font-semibold uppercase ${s.status ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'}`}>{s.status ? 'active' : 'inactive'}</span></td>
                    </tr>
                  ))}
                  {shifts.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No active shifts found.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}


      {activeModal && !activeModal.type && (
        <PenaltyForm initial={activeModal} onCancel={() => setActiveModal(null)} onSave={savePenalty} saving={saving} />
      )}
    </div>
  );
};

export default PayrollGovernanceCenter;
